import { Typography } from "@mui/material"
import { CarModelGeneralInfo, ModelOutSideDto } from "api"
import BreadCrumbsWrapper from "common/BreadCrumbs"
import PreSaleTag from "common/PreSaleTag"
import { setColor } from "components/shopFeatures/detailing/detailingSlice"
import { motion, useScroll } from "framer-motion"
import { useEffect, useMemo, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import { useDispatch } from "react-redux"
import ColorCircleAnimation from "reusable/colors/ColorCircleAnimation"
import { useTypedSelector } from "store"
import styled from "styled-components"
import { HEADER_ANIME_D, HEADER_ANIME_M, StyledSvgIcon } from "theme/shared"
import ColorsCarousel from "./ColorsCarousel"
import ModelInfo from "./ModelInfo"
import ThreeSixtyViewer from "./ThreeSixtyViewer"

const StyledBigWrapper = styled.div`
  height: 150vh;
  width: 100%;
`
const StyledMidWrapper = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  position: sticky;
  top: 0px;
`

const StyledSmallWrapper = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`

const StyledSectionWrapper = styled.div`
  z-index: 3;
  max-height: 100vh;
  height: 100vh;
  display: flex;
  padding-top: 50px;
  justify-content: start;
  align-items: center;
  position: relative;
  scroll-snap-align: center;
  perspective: 500px;
  z-index: 3;
  padding-bottom: 30px;
  flex-direction: column;
  padding-top: ${HEADER_ANIME_M}px;
  padding-bottom: 100px;
  overflow: hidden;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding-top: ${HEADER_ANIME_D}px;
  }
`

const StyledTopText = styled.div<{ $inView: boolean }>`
  margin: 40px 0px 0px;
  display: flex;
  align-items: center;
  flex-direction: column;
  transition: 2s;
  transform: ${({ $inView }) =>
    $inView ? "translateY(0)" : "translateY(-20px)"};
  opacity: ${({ $inView }) => ($inView ? "1" : "0")};
  transition-delay: 0.3s;
`
const StyledAnimationWrapper = styled.div`
  position: relative;
  height: 155px;
  width: 100%;
`
const StyledMotionDiv = styled(motion.div)`
  position: absolute;
  top: 0;
  width: 100%;
  max-width: 100vw;
`

const StyledColorCircleAnimation = styled(ColorCircleAnimation)`
  top: 20px;
  position: absolute;
  width: 100%;
  z-index: 10;
  pointer-events: none;
`
const StyledRotationIconWrap = styled.div`
  width: fit-content;
  margin: 0px auto;
`

const StyledPreSaleWrapper = styled.div`
  margin-bottom: 4px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-bottom: 8px;
  }
`

type EntrenchComponentProps = {
  outsideData: ModelOutSideDto
  generalInfo: CarModelGeneralInfo
  openCalculator: () => void
}

const EntrenchComponent = ({
  outsideData,
  generalInfo,
  openCalculator,
}: EntrenchComponentProps) => {
  const { ref: inViewRef, inView } = useInView({
    triggerOnce: true,
  })
  // selected color:
  const [selectedColorIndex, setSelectedColorIndex] = useState(0)
  const [colorCode, setColorCode] = useState("")
  // at the middle enter the color swiper
  const [colorChange, setColorChange] = useState(false)

  const ref = useRef<HTMLDivElement>(null)
  const colorRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const colorSectionRef = useRef(null)
  // Value being driven by scrolling (e.g. height)
  const thresholdY = 170 // set the scroll position where you want the state change

  const { scrollY } = useScroll()
  const { selectedColorCode } = useTypedSelector((state) => state.color)

  const [isPastThreshold, setIsPastThreshold] = useState(false)
  useEffect(
    () => scrollY.onChange((latest) => setIsPastThreshold(latest > thresholdY)),
    [scrollY, thresholdY]
  )

  const handleColorChange = (selectedColorIndex: number) => {
    setColorChange(true)
    const length = outsideData.colors.length
    setTimeout(() => {
      if (selectedColorIndex === length) setSelectedColorIndex(0)
      else setSelectedColorIndex(selectedColorIndex)
    }, 100)
    colorRef.current = setTimeout(() => setColorChange(false), 1800)
  }

  useEffect(() => {
    // Clear the interval when the component unmounts
    return () => {
      // clearTimeout(timerRef.current)
      if (colorRef.current) clearTimeout(colorRef.current)
    }
  }, [])

  useEffect(() => {
    if (selectedColorCode) {
      const index = outsideData.colors.findIndex(
        (color) => color.code === selectedColorCode
      )
      setSelectedColorIndex(index)
      setColorCode(selectedColorCode)
    }
  }, [selectedColorCode, outsideData])

  const imagesList = useMemo(() => {
    return outsideData?.colors[selectedColorIndex]?.images.map(
      (i) => i.imageUrl
    )
  }, [selectedColorIndex, outsideData?.colors])

  const dispatch = useDispatch()

  useEffect(() => {
    setColorCode(outsideData?.colors[selectedColorIndex]?.code)
    dispatch(setColor(outsideData?.colors[selectedColorIndex]?.code))
  }, [selectedColorIndex, outsideData, dispatch])

  return (
    <div ref={inViewRef}>
      <StyledBigWrapper ref={ref}>
        <StyledMidWrapper>
          <StyledSmallWrapper>
            <StyledSectionWrapper>
              <BreadCrumbsWrapper
                modelName={generalInfo.name}
                id={generalInfo.id}
              />
              <StyledTopText $inView={inView}>
                {generalInfo.typeId === 3 && (
                  <StyledPreSaleWrapper>
                    <PreSaleTag size="large" />
                  </StyledPreSaleWrapper>
                )}
                <Typography variant="h1">{generalInfo.name}</Typography>
                <Typography variant="h3" role={"presentation"}>
                  {generalInfo.secondaryTitle}
                </Typography>
              </StyledTopText>
              <ThreeSixtyViewer
                alt={`צבע ${colorCode}`}
                imageList={imagesList}
                enable={isPastThreshold}
              />
              {/* {selectedColorCode} */}
              {/* <StyledThreeSixtyWrapper
                alt={`צבע ${colorCode}`}
                imageList={imagesList}
                enable={isPastThreshold}
                selectedColorCode={colorCode}
              /> */}
              <StyledAnimationWrapper>
                <StyledMotionDiv
                  initial={{
                    opacity: 1,
                    scale: 1,
                    originY: 0,
                    visibility: "visible",
                  }}
                  animate={{
                    opacity: isPastThreshold ? 0 : 1,
                    scale: isPastThreshold ? 0.5 : 1,
                    translateY: isPastThreshold ? -100 : 0,
                    visibility: isPastThreshold ? "hidden" : "visible",
                  }}
                >
                  <ModelInfo
                    carData={generalInfo}
                    openCalculator={openCalculator}
                    onScrollDownClick={() => {
                      setTimeout(() => {
                        // focus on the  focusable element
                        const element = ref.current?.querySelector(
                          "#three-sixty-viewer"
                        )
                        if (element && "focus" in element) {
                          ;(element as HTMLElement).focus()
                        }
                      }, 100)
                    }}
                  />
                </StyledMotionDiv>
                <StyledMotionDiv
                  ref={colorSectionRef}
                  initial={{ opacity: 0, y: 100, visibility: "hidden" }}
                  animate={{
                    opacity: isPastThreshold ? 1 : 0,
                    y: isPastThreshold ? 0 : 100,
                    visibility: isPastThreshold ? "visible" : "hidden",
                  }}
                >
                  <StyledRotationIconWrap>
                    <StyledSvgIcon
                      src="/icons/rotate-360.svg"
                      alt="rotate"
                      $w={32}
                      $h={32}
                      role="presentation"
                      aria-hidden="true"
                    />
                  </StyledRotationIconWrap>
                  <ColorsCarousel
                    colorsData={outsideData?.colors}
                    handleColorChange={handleColorChange}
                    selectedColorIndex={selectedColorIndex}
                    colorChange={colorChange}
                    showStock={false}
                  />
                  <StyledColorCircleAnimation
                    animationFlag={colorChange}
                    hex={outsideData?.colors[selectedColorIndex]?.hex || ""}
                    animationTime="1800"
                  />
                </StyledMotionDiv>
              </StyledAnimationWrapper>
            </StyledSectionWrapper>
          </StyledSmallWrapper>
        </StyledMidWrapper>
      </StyledBigWrapper>
    </div>
  )
}

export default EntrenchComponent
